/**
 * EXIF Extractor Service Implementation
 * Uses ExifReader library to extract and process EXIF metadata from images
 */

import * as ExifReader from 'exifreader'
import {
  ExifExtractor,
  ExifData,
  ExifExtractionOptions,
  ExifKeywordSuggestions,
  ExifValidationResult,
  ExifExtractionError,
  EXIF_ERROR_CODES,
  ExifCameraSettings,
  ExifEquipmentInfo,
  ExifImageProperties,
  ExifLocationData,
  ExifDateTimeInfo,
  ExifTechnicalData,
  EXIF_ORIENTATION
} from '../contracts/exifExtractor'

export class ExifExtractorService implements ExifExtractor {
  private supportedTypes = [
    'image/jpeg',
    'image/jpg', 
    'image/tiff',
    'image/tif',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif'
  ]

  async extractExifData(file: File, options: ExifExtractionOptions = {}): Promise<ExifData> {
    try {
      if (!this.supportedTypes.includes(file.type)) {
        throw new ExifExtractionError(
          `Unsupported file type: ${file.type}`,
          EXIF_ERROR_CODES.UNSUPPORTED_FORMAT
        )
      }

      const buffer = await file.arrayBuffer()
      return this.extractExifFromBuffer(buffer, options)
    } catch (error) {
      if (error instanceof ExifExtractionError) {
        throw error
      }
      throw new ExifExtractionError(
        `Failed to extract EXIF data: ${error instanceof Error ? error.message : 'Unknown error'}`,
        EXIF_ERROR_CODES.FILE_READ_ERROR,
        { originalError: error }
      )
    }
  }

  async extractExifFromBuffer(buffer: ArrayBuffer, options: ExifExtractionOptions = {}): Promise<ExifData> {
    try {
      const tags = ExifReader.load(buffer, {
        expanded: true,
        includeUnknown: options.includeRawTags || false
      })

      if (!tags || Object.keys(tags).length === 0) {
        throw new ExifExtractionError(
          'No EXIF data found in image',
          EXIF_ERROR_CODES.NO_EXIF_DATA
        )
      }

      const exifData: ExifData = {
        camera: this.extractCameraSettings(tags),
        equipment: this.extractEquipmentInfo(tags),
        image: this.extractImageProperties(tags),
        location: this.extractLocationData(tags, options.convertCoordinates),
        dateTime: this.extractDateTimeInfo(tags),
        technical: this.extractTechnicalData(tags),
        rawTags: options.includeRawTags ? tags : undefined,
        extractedAt: new Date(),
        hasErrors: false,
        errors: []
      }

      if (options.validateData) {
        const validation = this.validateExifData(exifData)
        exifData.hasErrors = !validation.isValid
        exifData.errors = validation.errors
      }

      return exifData
    } catch (error) {
      if (error instanceof ExifExtractionError) {
        throw error
      }
      throw new ExifExtractionError(
        `Failed to parse EXIF data: ${error instanceof Error ? error.message : 'Unknown error'}`,
        EXIF_ERROR_CODES.PARSING_FAILED,
        { originalError: error }
      )
    }
  }

  private extractCameraSettings(tags: any): ExifCameraSettings {
    const exif = tags.exif || {}
    const ifd0 = tags.ifd0 || {}
    
    return {
      aperture: this.formatAperture(exif.FNumber?.description || exif.ApertureValue?.description),
      shutterSpeed: this.formatShutterSpeed(exif.ExposureTime?.description || exif.ShutterSpeedValue?.description),
      iso: this.parseNumber(exif.ISOSpeedRatings?.description || exif.PhotographicSensitivity?.description),
      focalLength: this.formatFocalLength(exif.FocalLength?.description),
      exposureMode: exif.ExposureMode?.description,
      meteringMode: exif.MeteringMode?.description,
      whiteBalance: exif.WhiteBalance?.description,
      flash: exif.Flash?.description,
      exposureCompensation: exif.ExposureBiasValue?.description,
      sceneMode: exif.SceneType?.description || exif.SceneCaptureType?.description
    }
  }

  private extractEquipmentInfo(tags: any): ExifEquipmentInfo {
    const ifd0 = tags.ifd0 || {}
    const exif = tags.exif || {}
    
    return {
      cameraMake: ifd0.Make?.description,
      cameraModel: ifd0.Model?.description,
      lensModel: exif.LensModel?.description || exif.LensSpecification?.description,
      serialNumber: exif.BodySerialNumber?.description || ifd0.SerialNumber?.description,
      firmwareVersion: ifd0.Software?.description,
      software: ifd0.ProcessingSoftware?.description || ifd0.Software?.description
    }
  }

  private extractImageProperties(tags: any): ExifImageProperties {
    const ifd0 = tags.ifd0 || {}
    const exif = tags.exif || {}
    
    return {
      width: this.parseNumber(ifd0.ImageWidth?.description || exif.ExifImageWidth?.description),
      height: this.parseNumber(ifd0.ImageLength?.description || exif.ExifImageLength?.description),
      orientation: this.parseNumber(ifd0.Orientation?.value),
      colorSpace: exif.ColorSpace?.description,
      bitDepth: this.parseNumber(ifd0.BitsPerSample?.description),
      compression: ifd0.Compression?.description,
      pixelXDimension: this.parseNumber(exif.PixelXDimension?.description),
      pixelYDimension: this.parseNumber(exif.PixelYDimension?.description)
    }
  }

  private extractLocationData(tags: any, convertCoordinates = true): ExifLocationData | undefined {
    const gps = tags.gps
    if (!gps) return undefined

    let latitude: number | undefined
    let longitude: number | undefined

    if (gps.GPSLatitude && gps.GPSLongitude) {
      if (convertCoordinates) {
        latitude = this.convertGPSToDecimal(
          gps.GPSLatitude.description,
          gps.GPSLatitudeRef?.description || 'N'
        )
        longitude = this.convertGPSToDecimal(
          gps.GPSLongitude.description,
          gps.GPSLongitudeRef?.description || 'E'
        )
      }
    }

    return {
      latitude,
      longitude,
      altitude: this.parseNumber(gps.GPSAltitude?.description),
      gpsDirection: this.parseNumber(gps.GPSImgDirection?.description),
      gpsDateStamp: gps.GPSDateStamp?.description,
      gpsTimeStamp: gps.GPSTimeStamp?.description
    }
  }

  private extractDateTimeInfo(tags: any): ExifDateTimeInfo {
    const ifd0 = tags.ifd0 || {}
    const exif = tags.exif || {}
    
    return {
      dateTimeOriginal: this.parseDate(exif.DateTimeOriginal?.description),
      dateTimeDigitized: this.parseDate(exif.DateTimeDigitized?.description),
      createDate: this.parseDate(ifd0.DateTime?.description),
      modifyDate: this.parseDate(ifd0.DateTime?.description),
      offsetTime: exif.OffsetTime?.description,
      offsetTimeOriginal: exif.OffsetTimeOriginal?.description,
      offsetTimeDigitized: exif.OffsetTimeDigitized?.description
    }
  }

  private extractTechnicalData(tags: any): ExifTechnicalData {
    const exif = tags.exif || {}
    
    return {
      subjectDistance: exif.SubjectDistance?.description,
      lightSource: exif.LightSource?.description,
      digitalZoomRatio: this.parseNumber(exif.DigitalZoomRatio?.description),
      saturation: exif.Saturation?.description,
      contrast: exif.Contrast?.description,
      sharpness: exif.Sharpness?.description,
      gainControl: exif.GainControl?.description,
      sensingMethod: exif.SensingMethod?.description,
      fileSource: exif.FileSource?.description,
      sceneType: exif.SceneType?.description
    }
  }

  generateKeywordSuggestions(exifData: ExifData): ExifKeywordSuggestions {
    const suggestions: ExifKeywordSuggestions = {
      camera: [],
      lens: [],
      settings: [],
      location: [],
      technical: []
    }

    // Camera-based keywords
    if (exifData.equipment.cameraMake) {
      suggestions.camera?.push(exifData.equipment.cameraMake.toLowerCase())
    }
    if (exifData.equipment.cameraModel) {
      if (exifData.equipment.cameraModel.includes('DSLR') || exifData.equipment.cameraModel.includes('EOS')) {
        suggestions.camera?.push('DSLR')
      }
      if (exifData.equipment.cameraModel.includes('mirrorless')) {
        suggestions.camera?.push('mirrorless')
      }
    }

    // Lens-based keywords
    if (exifData.camera.focalLength) {
      const focal = parseInt(exifData.camera.focalLength)
      if (focal < 35) suggestions.lens?.push('wide angle')
      else if (focal > 85) suggestions.lens?.push('telephoto')
      else if (focal >= 35 && focal <= 85) suggestions.lens?.push('standard')
      
      if (focal > 100) suggestions.lens?.push('zoom')
      if (focal <= 24) suggestions.lens?.push('ultra wide')
    }

    // Settings-based keywords
    if (exifData.camera.aperture) {
      const aperture = parseFloat(exifData.camera.aperture.replace('f/', ''))
      if (aperture <= 2.8) suggestions.settings?.push('shallow depth of field')
      if (aperture >= 8) suggestions.settings?.push('deep focus')
    }

    if (exifData.camera.iso && exifData.camera.iso > 1600) {
      suggestions.settings?.push('high ISO')
    }

    if (exifData.camera.shutterSpeed) {
      if (exifData.camera.shutterSpeed.includes('1/') && parseInt(exifData.camera.shutterSpeed.split('/')[1]) > 500) {
        suggestions.settings?.push('fast shutter')
      }
      if (!exifData.camera.shutterSpeed.includes('/') || exifData.camera.shutterSpeed.includes('1/') && parseInt(exifData.camera.shutterSpeed.split('/')[1]) < 30) {
        suggestions.settings?.push('long exposure')
      }
    }

    // Technical keywords
    if (exifData.camera.flash?.includes('Fired')) {
      suggestions.technical?.push('flash photography')
    }

    return suggestions
  }

  formatForDisplay(exifData: ExifData): Record<string, string> {
    const display: Record<string, string> = {}

    // Camera settings
    if (exifData.camera.aperture) display['Aperture'] = exifData.camera.aperture
    if (exifData.camera.shutterSpeed) display['Shutter Speed'] = exifData.camera.shutterSpeed
    if (exifData.camera.iso) display['ISO'] = exifData.camera.iso.toString()
    if (exifData.camera.focalLength) display['Focal Length'] = exifData.camera.focalLength

    // Equipment
    if (exifData.equipment.cameraMake && exifData.equipment.cameraModel) {
      display['Camera'] = `${exifData.equipment.cameraMake} ${exifData.equipment.cameraModel}`
    }
    if (exifData.equipment.lensModel) display['Lens'] = exifData.equipment.lensModel

    // Image properties
    if (exifData.image.width && exifData.image.height) {
      display['Dimensions'] = `${exifData.image.width} × ${exifData.image.height}`
    }
    if (exifData.image.orientation) {
      display['Orientation'] = EXIF_ORIENTATION[exifData.image.orientation as keyof typeof EXIF_ORIENTATION] || 'Unknown'
    }

    // Date/time
    if (exifData.dateTime.dateTimeOriginal) {
      display['Date Taken'] = exifData.dateTime.dateTimeOriginal.toLocaleDateString()
    }

    // Location
    if (exifData.location?.latitude && exifData.location?.longitude) {
      display['GPS Coordinates'] = `${exifData.location.latitude.toFixed(6)}, ${exifData.location.longitude.toFixed(6)}`
    }

    return display
  }

  async hasExifData(file: File): Promise<boolean> {
    try {
      const buffer = await file.arrayBuffer()
      const tags = ExifReader.load(buffer)
      return tags && Object.keys(tags).length > 0
    } catch {
      return false
    }
  }

  getSupportedTypes(): string[] {
    return [...this.supportedTypes]
  }

  validateExifData(exifData: ExifData): ExifValidationResult {
    const warnings: string[] = []
    const errors: string[] = []
    const suggestions: string[] = []

    // Check for missing essential data
    if (!exifData.dateTime.dateTimeOriginal) {
      warnings.push('Date/time information missing')
    }

    if (!exifData.equipment.cameraMake || !exifData.equipment.cameraModel) {
      warnings.push('Camera information incomplete')
    }

    if (!exifData.camera.iso && !exifData.camera.aperture && !exifData.camera.shutterSpeed) {
      warnings.push('Camera settings information missing')
    }

    // Check for data quality
    if (exifData.camera.iso && exifData.camera.iso > 6400) {
      suggestions.push('High ISO detected - consider noise reduction keywords')
    }

    if (exifData.location && exifData.location.latitude && exifData.location.longitude) {
      suggestions.push('GPS location available - consider adding location-based keywords')
    }

    return {
      isValid: errors.length === 0,
      warnings,
      errors,
      suggestions
    }
  }

  // Helper methods
  private formatAperture(value?: string): string | undefined {
    if (!value) return undefined
    if (value.startsWith('f/')) return value
    const num = parseFloat(value)
    return isNaN(num) ? value : `f/${num}`
  }

  private formatShutterSpeed(value?: string): string | undefined {
    if (!value) return undefined
    if (value.includes('/') || value.includes('s')) return value
    const num = parseFloat(value)
    if (isNaN(num)) return value
    return num >= 1 ? `${num}s` : `1/${Math.round(1/num)}`
  }

  private formatFocalLength(value?: string): string | undefined {
    if (!value) return undefined
    if (value.includes('mm')) return value
    const num = parseFloat(value)
    return isNaN(num) ? value : `${num}mm`
  }

  private parseNumber(value?: string | number): number | undefined {
    if (typeof value === 'number') return value
    if (!value) return undefined
    const num = parseFloat(value.toString())
    return isNaN(num) ? undefined : num
  }

  private parseDate(value?: string): Date | undefined {
    if (!value) return undefined
    try {
      // EXIF date format: "YYYY:MM:DD HH:MM:SS"
      const formatted = value.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3')
      const date = new Date(formatted)
      return isNaN(date.getTime()) ? undefined : date
    } catch {
      return undefined
    }
  }

  private convertGPSToDecimal(coordinate: string, reference: string): number | undefined {
    try {
      // Parse DMS format: "40° 42' 46.11""
      const parts = coordinate.match(/(\d+)°?\s*(\d+)'?\s*([\d.]+)"?/)
      if (!parts) return undefined

      const degrees = parseFloat(parts[1])
      const minutes = parseFloat(parts[2])
      const seconds = parseFloat(parts[3])

      let decimal = degrees + minutes/60 + seconds/3600

      // Apply hemisphere
      if (reference === 'S' || reference === 'W') {
        decimal = -decimal
      }

      return decimal
    } catch {
      return undefined
    }
  }
}